import React, { useRef, useEffect } from 'react';

import { RefreshIcon, CameraSwitchIcon, CloseIconWhite } from 'src/assets/images';
import { LoaderAnimation } from 'src/components';
import ReactDOM from 'react-dom';
import ImageCapture, {
  DeviceInfo,
  ImageCaptureResolutionHint,
  ImageMirrorBehavior,
  RenderProps,
  ResolutionOptionType,
} from '../image-capture/image-capture';
import ShowIf from '../show-if/show-if';
import TealiumEvents from 'src/helpers/tealium-events';
import './document-image-capture-modal.scss';

type Props = {
  show: boolean;
  onImageCapture: (imageSrc: string) => void;
  refreshTagging?: () => void;
  customerType?: string;
  automationIdPrefix: string;
  switchTagging?: () => void;
  onHide: () => void;
  disabled?: boolean;
};

export default function DocumentImageCaptureModal(props: Props) {
  const topOfPage = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // @ts-ignore
    document.getElementById('modal-root').style.display = props.show ? 'block' : 'none';

    if (props.show) {
      if (topOfPage.current) {
        topOfPage.current.scrollIntoView(true);
      }
      TealiumEvents.docsOpenCameraModal(props.customerType);
    }
  }, [props.show]);

  const modalBottom = (isMobile = false, renderProps) => (
    <>
      <p className={`selfie-text has-padding-bottom-6 ${isMobile ? '' : 'has-padding-top-3'}`}>
        Position your document clearly in front of the camera
      </p>
      <div>
        <button
          className="refresh-button"
          disabled={renderProps.status.isRefreshingDeviceList}
          onClick={() => {
            renderProps.commands.refreshDeviceList();
            props.refreshTagging && props.refreshTagging();
          }}
        >
          {renderProps.status.isRefreshingDeviceList ? (
            <LoaderAnimation height={30} width={30} />
          ) : (
            <img src={RefreshIcon} />
          )}
        </button>
        <button
          disabled={renderProps.status.isDeviceLoading}
          className="button take-photo-button"
          onClick={() => {
            renderProps.commands.captureImage();
          }}
          automationid={`${props.automationIdPrefix}_Anchor_Capture_Button`}
        >
          Take photo
        </button>
        {renderProps.status.devicesTotalCount > 1 && (
          <button
            className="switch-camera-button"
            onClick={() => {
              renderProps.commands.switchDevice();
              if (props.switchTagging) props.switchTagging();
            }}
          >
            <img src={CameraSwitchIcon} />
          </button>
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(
    <>
      {props.show && (
        <ImageCapture
          onDeviceInfoChanged={(deviceInfo: DeviceInfo) => {
            if (deviceInfo?.video?.captureResolution) {
              // @ts-ignore
              document.getElementById('modal-root').style.setProperty(
                '--aspect-ratio', // @ts-ignore
                deviceInfo.video.captureResolution.width /
                  deviceInfo.video.captureResolution.height,
              );
            }
          }}
          imageCaptureResolutionHint={
            { type: ResolutionOptionType.MAXIMUM } as ImageCaptureResolutionHint
          }
          onImageCaptured={(base64Image: string) => {
            props.onImageCapture(base64Image);
            props.onHide();
          }}
          imageMirrorBehavior={ImageMirrorBehavior.NO_FLIPPING}
        >
          {(renderProps: RenderProps) => {
            return (
              <div
                className={`DocumentImageCaptureModal modal ${props.show ? 'is-active' : ''}`}
                automationid={`${props.automationIdPrefix}_ImageCaptureModal_Main`}
              >
                <div className="modal-container">
                  <div className="webcam-container">
                    <button
                      ref={topOfPage}
                      className="absolute cursor-pointer right-4 md:right-0 top-10 md:top-0 z-99"
                      disabled={props.disabled}
                      onClick={() => {
                        if (!props.disabled) {
                          props.onHide();
                        }
                      }}
                      automationid={`${props.automationIdPrefix}_Close_Modal_Button`}
                    >
                      <img src={CloseIconWhite} alt="close" />
                    </button>

                    <div className="webcam-component">
                      {renderProps.renderFunctions.drawCameraDisplay()}

                      <ShowIf
                        condition={
                          renderProps.status.isDeviceLoading ||
                          renderProps.status.isRefreshingDeviceList
                        }
                      >
                        <div className="webcam-loader-container">
                          <div className="webcam-loader">
                            <LoaderAnimation />
                          </div>
                        </div>
                      </ShowIf>
                    </div>

                    <div className="modal-bottom-mobile">{modalBottom(false, renderProps)}</div>
                  </div>
                </div>
              </div>
            );
          }}
        </ImageCapture>
      )}
    </>, // @ts-ignore
    document.getElementById('modal-root'),
  );
}
